import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FINANCIAL_TRANSACTION_TYPE, MODE_OF_PAYMENT } from 'khufu-legos';
import { CloseButton, Form, Modal as ModalComponent } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { createFinancialTransaction } from '../../apis/queries';
import SubmitButton from '../../components/Buttons/SubmitButton';
import DisplayFieldErrors from '../../utils/DisplayFormError';
import { notification, NotificationKinds } from '../../widgets/Notifications/notifications';
import { FINANCIAL_TRANSACTION_QUERY_KEY } from './FinancialTransaction';

interface CreateFinancialTransactionModalProps {
  show: boolean;
  handleClose: () => void;
}

interface CreateFinancialTransactionFormInput {
  remarks: string;
  amount: number;
  type: FINANCIAL_TRANSACTION_TYPE;
  action: string;
  modeOfPayment: MODE_OF_PAYMENT;
}

const CreateFinancialTransactionModal = ({ show, handleClose }: CreateFinancialTransactionModalProps) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (financialTransaction: CreateFinancialTransactionFormInput) => {
      return createFinancialTransaction(financialTransaction);
    },
    onSuccess: data => {
      const { data: newFinancialTransaction } = data;
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `FINANCIAL TRANSACTION CREATED`,
        subtitle: `Financial Transaction for ${newFinancialTransaction?.amount} has been added`
      });

      handleClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FINANCIAL_TRANSACTION_QUERY_KEY });
    }
  });

  const loading = mutation?.isPending;

  const methods = useForm<CreateFinancialTransactionFormInput>({
    mode: 'onTouched',
    defaultValues: {
      remarks: '',
      type: FINANCIAL_TRANSACTION_TYPE.EXPENDITURE,
      modeOfPayment: MODE_OF_PAYMENT.CASH,
      action: 'expense'
    }
  });

  const {
    getValues,
    register,
    formState: { isValid, errors },
    setValue,
    watch
  } = methods;

  const onCreateFinancialTransaction = () => {
    const fields = getValues();
    mutation.mutate(fields);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = e.target.value as FINANCIAL_TRANSACTION_TYPE;
    setValue('type', selectedType);

    // Set the action based on the selected type
    const actionValue = selectedType === FINANCIAL_TRANSACTION_TYPE.EXPENDITURE ? 'expense' : 'excesses';
    setValue('action', actionValue);
  };

  const handleModeOfPayment = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValue('modeOfPayment', e.target.value as MODE_OF_PAYMENT);
  };

  return (
    <ModalComponent show={show} onHide={handleClose} centered>
      <ModalComponent.Header>
        <h4 className="card-header-title">New Financial Transaction</h4>
        <CloseButton onClick={handleClose} />
      </ModalComponent.Header>
      <ModalComponent.Body className="overflow-auto d-flex flex-column flex-grow-1">
        <Form>
          <div className="form-group">
            <Form.Label htmlFor="type">Type</Form.Label>
            <Form.Select id="type" {...register('type', { required: true })} onChange={handleTypeChange}>
              <option value="expenditure">Expenditure</option>
              <option value="inflow">Inflow</option>
            </Form.Select>
            {errors?.type && <DisplayFieldErrors displayText={errors.type.message!} />}
          </div>

          <div className="form-group">
            <Form.Label htmlFor="action">Action</Form.Label>
            <Form.Select id="action" {...register('action', { required: true })}>
              {watch('type') === FINANCIAL_TRANSACTION_TYPE.EXPENDITURE && (
                <>
                  <option value="expense">expense</option>
                  <option value="cashBack">cash back</option>
                </>
              )}
              {watch('type') === FINANCIAL_TRANSACTION_TYPE.INFLOW && (
                <>
                  <option value="excesses">excesses</option>
                </>
              )}
            </Form.Select>
            {errors?.action && <DisplayFieldErrors displayText={errors.action.message!} />}
          </div>

          <div className="form-group">
            <Form.Label htmlFor="modeOfPayment">Mode Of Payment</Form.Label>
            <Form.Select
              id="modeOfPayment"
              {...register('modeOfPayment', { required: true })}
              onChange={handleModeOfPayment}
            >
              <option value="cash">Cash</option>
              <option value="transfer">Transfer</option>
              <option value="credit">Credit</option>
            </Form.Select>
            {errors?.modeOfPayment && <DisplayFieldErrors displayText={errors.modeOfPayment.message!} />}
          </div>
          <div className="form-group">
            <Form.Label htmlFor="amount">Amount</Form.Label>
            <Form.Control
              type="number"
              id="amount"
              {...register('amount', { required: true, min: 1, valueAsNumber: true })}
            />
            {errors?.amount && <DisplayFieldErrors displayText={errors.amount.message!} />}
          </div>
          <div className="form-group">
            <Form.Label htmlFor="remark">Remark</Form.Label>
            <Form.Control type="text" id="remarks" {...register('remarks', { required: true })} />
            {errors?.remarks && <DisplayFieldErrors displayText={errors.remarks.message!} />}
          </div>
          <SubmitButton
            buttonText="Create Financial Transaction"
            loading={loading}
            onClick={onCreateFinancialTransaction}
            disabled={!isValid || loading}
          />
        </Form>
      </ModalComponent.Body>
    </ModalComponent>
  );
};

export default CreateFinancialTransactionModal;
